import type { EmergencyAddress } from "@dialstack/sdk-webrtc";

import { US_STATES } from "constants/addresses";

// The subset of address fields shared by EmergencyAddressDetails (what the
// provider returns) and EmergencyAddressInput (what our create form submits),
// so both can be canonicalized by the same code.
type AddressFields = {
  address_number?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
};

// "New York" -> "NY". `US_STATES.data` is already `{ NY: "New York", … }`, so
// invert it rather than hand-writing a 50-state table that would drift from
// the rest of the app.
const STATE_CODE_BY_NAME = new Map(
  Object.entries(US_STATES.data).map(([code, name]) => [
    name.toLowerCase(),
    code
  ])
);

export function normalizeStateCode(state: string | undefined): string {
  const trimmed = (state ?? "").trim();
  if (!trimmed) return "";
  const byName = STATE_CODE_BY_NAME.get(trimmed.toLowerCase());
  if (byName) return byName;
  return trimmed.toUpperCase();
}

// Lowercase, drop punctuation, collapse whitespace, ordinals to digits.
// Deliberately NOT normalizing street-type ("ave"/"avenue"), directional or
// unit-word ("ste"/"apt") synonyms: those are false-merge vectors, and `Ste 300`
// vs `Apt 300` can be different floors of the same building. Prefix subsumption
// below already catches the abbreviation case that actually occurs.
function canonicalizeText(value: string | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\b(\d+)(?:st|nd|rd|th)\b/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export type CanonicalAddress = {
  street: string;
  streetTokens: string[];
  // Everything that must match before two street lines are even comparable.
  scopeKey: string;
  formatted: string;
  coords: string | null;
};

export function canonicalizeAddress(fields: AddressFields): CanonicalAddress {
  const street = canonicalizeText(fields.street);
  const zip5 = (fields.postal_code ?? "").replace(/\D/g, "").slice(0, 5);
  const hasCoords =
    typeof fields.latitude === "number" && typeof fields.longitude === "number";
  return {
    street,
    streetTokens: street ? street.split(" ") : [],
    scopeKey: [
      canonicalizeText(fields.address_number),
      canonicalizeText(fields.unit),
      canonicalizeText(fields.city),
      normalizeStateCode(fields.state),
      zip5
    ].join("|"),
    formatted: canonicalizeText(fields.formatted_address),
    coords: hasCoords
      ? `${fields.latitude!.toFixed(5)},${fields.longitude!.toFixed(5)}`
      : null
  };
}

// Which rule collapsed a pair, strongest first — the provider's own judgement
// outranks anything we derive from the typed strings.
export type MergeReason = "coords" | "formatted" | "exact" | "prefix";
const REASON_RANK: Record<MergeReason, number> = {
  coords: 0,
  formatted: 1,
  exact: 2,
  prefix: 3
};

function isStrictPrefixOf(shorter: string[], longer: string[]): boolean {
  if (shorter.length === 0 || shorter.length >= longer.length) return false;
  return shorter.every((token, index) => token === longer[index]);
}

export function isSameCanonicalAddress(
  a: CanonicalAddress,
  b: CanonicalAddress
): boolean {
  return a.scopeKey === b.scopeKey && a.street === b.street;
}

function mergeReason(
  a: CanonicalAddress,
  b: CanonicalAddress,
  // How many different ways each side's street line gets continued by the rest
  // of the list — see `countPrefixBranches`.
  aBranches: number,
  bBranches: number
): MergeReason | null {
  // Two geocoded rows are the same place if and only if the carrier put them at
  // the same point. MSAG validation outranks our string matching, so a
  // coordinate mismatch vetoes the component tiers entirely.
  if (a.coords !== null && b.coords !== null)
    return a.coords === b.coords ? "coords" : null;
  if (a.formatted && b.formatted && a.formatted === b.formatted)
    return "formatted";
  if (a.scopeKey !== b.scopeKey) return null;
  if (a.street === b.street) return "exact";
  const [shorter, longer, shorterBranches] =
    a.streetTokens.length < b.streetTokens.length
      ? [a, b, aBranches]
      : [b, a, bBranches];
  if (!isStrictPrefixOf(shorter.streetTokens, longer.streetTokens)) return null;
  // "500 7th" sits inside both "500 7th Ave" and "500 7th St" — it names no
  // single place, so it merges with neither.
  if (shorterBranches > 1) return null;
  return "prefix";
}

/**
 * How many different streets a short street line could be naming, judged by the
 * distinct token that follows it in the rest of the list.
 *
 * "500 7th" alongside "500 7th Ave" and "500 7th St" branches two ways (`ave`,
 * `st`), so it names no single place and must not merge into either. Alongside
 * "500 7th Ave" and "500 7th Ave West" it branches one way (`ave`) — every
 * continuation agrees, so the chain is one location typed three times.
 */
function countPrefixBranches(
  candidate: CanonicalAddress,
  all: CanonicalAddress[]
): number {
  const followingTokens = new Set<string>();
  all.forEach((other) => {
    if (candidate.scopeKey !== other.scopeKey) return;
    if (!isStrictPrefixOf(candidate.streetTokens, other.streetTokens)) return;
    followingTokens.add(other.streetTokens[candidate.streetTokens.length]);
  });
  return followingTokens.size;
}

export type DedupedAddress = {
  /** The record to confirm against — the most complete of the group. */
  address: EmergencyAddress;
  /** Collapsed near-identical records, in their original list order. */
  duplicates: EmergencyAddress[];
  /** Which rule collapsed them; null when nothing was merged. */
  mergedBy: MergeReason | null;
};

function componentScore(fields: AddressFields): number {
  return [
    fields.address_number,
    fields.street,
    fields.unit,
    fields.city,
    fields.state,
    fields.postal_code
  ].filter((value) => Boolean(value && value.trim())).length;
}

// The record a 911 dispatch should be sent against: provider-validated first,
// then the most complete, then the most specific street line, then the most
// recently added. The street-line tier is what keeps "500 7th Ave" rather than
// the "500 7th" that merged into it — the group exists precisely because one
// spelling is fuller than the other, and the fuller one is what a dispatcher
// needs to read.
function pickSurvivor(group: EmergencyAddress[]): EmergencyAddress {
  return [...group].sort((a, b) => {
    const validated =
      Number(Boolean(b.address.formatted_address)) -
      Number(Boolean(a.address.formatted_address));
    if (validated !== 0) return validated;
    const completeness = componentScore(b.address) - componentScore(a.address);
    if (completeness !== 0) return completeness;
    const specificity =
      canonicalizeAddress(b.address).streetTokens.length -
      canonicalizeAddress(a.address).streetTokens.length;
    if (specificity !== 0) return specificity;
    return (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0);
  })[0];
}

/**
 * Collapse saved emergency addresses that describe the same physical place, so
 * the picker offers one row per location instead of one row per spelling.
 *
 * Groups keep their first-appearance order, so the list does not reshuffle as
 * the provider validates rows.
 */
export function dedupeEmergencyAddresses(
  addresses: EmergencyAddress[]
): DedupedAddress[] {
  const canonical = addresses.map((entry) =>
    canonicalizeAddress(entry.address)
  );
  const branchCounts = canonical.map((candidate) =>
    countPrefixBranches(candidate, canonical)
  );

  // Union-find so a chain of pairwise matches lands in one group.
  const parent = addresses.map((_, index) => index);
  const find = (index: number): number => {
    let current = index;
    while (parent[current] !== current) {
      parent[current] = parent[parent[current]];
      current = parent[current];
    }
    return current;
  };
  const pairReasons: { a: number; b: number; reason: MergeReason }[] = [];

  for (let i = 0; i < addresses.length; i += 1) {
    for (let j = i + 1; j < addresses.length; j += 1) {
      const reason = mergeReason(
        canonical[i],
        canonical[j],
        branchCounts[i],
        branchCounts[j]
      );
      if (!reason) continue;
      pairReasons.push({ a: i, b: j, reason });
      const rootA = find(i);
      const rootB = find(j);
      if (rootA !== rootB) parent[rootB] = rootA;
    }
  }

  const groups = new Map<number, number[]>();
  addresses.forEach((_, index) => {
    const root = find(index);
    const existing = groups.get(root);
    if (existing) existing.push(index);
    else groups.set(root, [index]);
  });

  return [...groups.values()].map((indices) => {
    const members = indices.map((index) => addresses[index]);
    const survivor = pickSurvivor(members);
    const reasons = pairReasons
      .filter(({ a, b }) => indices.includes(a) && indices.includes(b))
      .map(({ reason }) => reason)
      .sort((a, b) => REASON_RANK[a] - REASON_RANK[b]);
    return {
      address: survivor,
      duplicates: members.filter((member) => member.id !== survivor.id),
      mergedBy: reasons[0] ?? null
    };
  });
}

/**
 * The saved address that is component-for-component identical to `fields`, if
 * any. Used only to offer "use the one you already have" before creating a
 * near-duplicate — never to silently substitute it, since a typed address that
 * differs at all may be a genuinely different place.
 */
export function findExactMatch(
  fields: AddressFields,
  addresses: EmergencyAddress[]
): EmergencyAddress | null {
  const target = canonicalizeAddress(fields);
  return (
    addresses.find((entry) =>
      isSameCanonicalAddress(canonicalizeAddress(entry.address), target)
    ) ?? null
  );
}

/** Full single-line address for labels and screen readers. */
export function formatAddress(fields: AddressFields): string {
  const validated = fields.formatted_address?.trim();
  if (validated) return validated;
  const streetLine = [fields.address_number, fields.street, fields.unit]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  const stateZip = [fields.state?.trim(), fields.postal_code?.trim()]
    .filter(Boolean)
    .join(" ");
  const cityLine = [fields.city?.trim(), stateZip].filter(Boolean).join(", ");
  return [streetLine, cityLine].filter(Boolean).join(", ");
}

/** Just enough to recognise the place in one line of panel chrome. */
export function shortAddressLabel(fields: AddressFields): string {
  const streetLine = [fields.address_number, fields.street]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return streetLine || fields.city?.trim() || formatAddress(fields);
}
